import { ClockIcon, DepositIcon, EmailResendIcon } from "@xola/icons";
import { Button, Modal } from "@xola/ui-kit";
import clsx from "clsx";
import { isExperiencePurchaseItem, isOtherSellerPurchase } from "common";
import React, { ReactElement } from "react";
import { Affiliate, PartnerObject, Permission, Purchase } from "types";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useResource } from "@/hooks/useResource";
import { getSeller } from "@/utils/getSeller";

export enum CollectBalanceOption {
    COLLECT_NOW = "Collect Balance Now",
    COLLECT_VIA_INVOICE = "Collect Balance via Invoice",
    COLLECT_LATER = "Collect Balance Later",
}

const CollectOptionCopy: { [key in CollectBalanceOption]: string } = {
    [CollectBalanceOption.COLLECT_NOW]: "Use standard methods of collecting balance",
    [CollectBalanceOption.COLLECT_VIA_INVOICE]: "Send an invoice to the organizer",
    [CollectBalanceOption.COLLECT_LATER]: "Collect balance later",
};

interface CollectBalanceOptionsProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (option: CollectBalanceOption) => void;
    purchase: Purchase;
    showPayLater?: boolean;
}

export const CollectBalanceOptionsModal = ({
    isOpen,
    onConfirm,
    onClose,
    purchase,
    showPayLater = true,
}: CollectBalanceOptionsProps) => {
    const { isGranted } = useAuthorization();
    const seller = getSeller();
    const isAffiliatePurchase = !!(purchase.partner?.id && purchase.partner?.object === PartnerObject.AFFILIATE);
    const { data: affiliate } = useResource<Affiliate>(["affiliates", purchase.partner?.id], null, {
        enabled: isAffiliatePurchase,
    });

    const affiliateCanReceiveInvoice = affiliate?.affiliateNotification?.enabled && !!affiliate?.email;
    const hasExperienceItem = purchase.items.some((item) => isExperiencePurchaseItem(item));
    const canSendInvoice =
        isGranted(Permission.INVOICE_VIEW) &&
        (!!purchase.customerEmail || affiliateCanReceiveInvoice) &&
        hasExperienceItem;

    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <Modal.Header>How Do You Want To Collect Balance?</Modal.Header>

            <Modal.Body>
                <CollectBalanceOptionItem
                    name={CollectBalanceOption.COLLECT_NOW}
                    icon={<DepositIcon size="large" />}
                    description={CollectOptionCopy[CollectBalanceOption.COLLECT_NOW]}
                    onClick={() => {
                        onConfirm(CollectBalanceOption.COLLECT_NOW);
                    }}
                />
                {canSendInvoice && !isOtherSellerPurchase(purchase, seller.id) ? (
                    <CollectBalanceOptionItem
                        name={CollectBalanceOption.COLLECT_VIA_INVOICE}
                        icon={<EmailResendIcon size="large" />}
                        description={CollectOptionCopy[CollectBalanceOption.COLLECT_VIA_INVOICE]}
                        onClick={() => {
                            onConfirm(CollectBalanceOption.COLLECT_VIA_INVOICE);
                        }}
                    />
                ) : null}

                {showPayLater && (
                    <CollectBalanceOptionItem
                        name={CollectBalanceOption.COLLECT_LATER}
                        icon={<ClockIcon size="large" />}
                        description={CollectOptionCopy[CollectBalanceOption.COLLECT_LATER]}
                        onClick={() => {
                            onConfirm(CollectBalanceOption.COLLECT_LATER);
                        }}
                    />
                )}
            </Modal.Body>

            <Modal.Footer className="space-x-2">
                <Button color="primary" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

interface CollectBalanceOptionItemProps {
    name: string;
    description: string;
    icon: ReactElement;
    onClick: () => void;
}

const CollectBalanceOptionItem = ({ name, description, icon, onClick, ...rest }: CollectBalanceOptionItemProps) => {
    return (
        <div
            className={clsx(
                "mb-4 flex cursor-pointer space-x-5 rounded border border-gray py-4 pr-6",
                "hover:border-black hover:bg-gray-lighter",
            )}
            role="button"
            onClick={onClick}
            {...rest}
        >
            <div className="flex items-center pr-2 pl-7 text-gray-darker">{icon}</div>

            <div className="text-left">
                <p className="pb-2 text-md font-semibold">{name}</p>
                <span className="text-gray-darker">{description}</span>
            </div>
        </div>
    );
};
